import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { OnboardingLayout } from 'layouts/OnboardingLayout';
import { useTranslation } from 'hooks/useTranslation';
import { useMetrics } from 'hooks/metrics/useMetrics';
import './style.scss';
import { OnboardingLoading } from 'components/onboarding/components/OnboardingLoading';
import { renderMaxLengthText } from 'utils/renderMaxLengthText';
import { OnboardingButton } from 'components/onboarding/components/DefaultButton';
import { getClaroAdsKey } from 'utils/claroAds/getClaroAdskey';
import { usePaymentMethods } from '../hooks/usePaymentMethods';
import { getDeviceParams } from '../utils/deviceParams';
import { QR_LINKING_ACCOUNT_KEYS } from '../translationKeys';
import {
  QR_LINKING,
  QR_LINKING_LINKING_ERROR_TIME_EXCEEDED,
  QR_LINKING_PAYMENT_METHOD,
} from 'routes/RouteConstants';

export const QRLinkingAccount = () => {
  const { state, search } = useLocation();
  const navigate = useNavigate();
  const qrUserData = state?.qrUserData;
  const mdpReturn = state?.mdpReturn;
  // La query se reenvía tal cual desde QRLinking (ver comentario más abajo),
  // así que sigue siendo la fuente de verdad; state.deviceParams es el respaldo.
  const deviceParams = getDeviceParams(
    new URLSearchParams(search),
    state?.deviceParams,
  );

  const { buildMetricsEvent, sendMetricsEvent } = useMetrics();
  const { translationMapper } = useTranslation();
  const translatedTexts = useMemo(
    () => translationMapper(QR_LINKING_ACCOUNT_KEYS),
    [translationMapper],
  );

  const [isFinishing, setIsFinishing] = useState(false);
  const { fetchPaymentMethods } = usePaymentMethods();

  // Sin qrUserData no hay sesión QR (entrada directa a la URL, F5, o history
  // perdido): no hay con qué vincular, así que se manda de vuelta al código
  // en vez de pintar la pantalla vacía.
  const hasQrSession = Boolean(qrUserData?.user_token);

  useEffect(() => {
    if (hasQrSession) return;

    navigate(QR_LINKING, { replace: true });
  }, [hasQrSession, navigate]);

  if (!hasQrSession) return null;

  const onFinishAction = async () => {
    setIsFinishing(true);

    try {
      const paymentMethodsData = await fetchPaymentMethods(
        qrUserData.user_token,
        deviceParams,
      );

      setIsFinishing(false);

      // La query se reenvía tal cual: los params del BRF deben sobrevivir hasta
      // el selector aunque location.state se pierda.
      // replace: true (igual que el resto de los saltos del flujo, ver
      // QRLinking/index.js): este paso pisa a /validar-cuenta en el historial
      // en vez de apilarse, para que "back" desde el final no caiga acá.
      return navigate(
        { pathname: QR_LINKING_PAYMENT_METHOD, search },
        {
          replace: true,
          state: {
            qrUserData,
            mdpReturn,
            paymentMethodsData,
            transactionParams: state?.transactionParams,
            deviceParams,
          },
        },
      );
    } catch (error) {
      setIsFinishing(false);

      return navigate(QR_LINKING_LINKING_ERROR_TIME_EXCEEDED, {
        state: { fromQR: true },
        replace: true,
      });
    }
  };

  return (
    <Fragment>
      <OnboardingLoading show={isFinishing} />
      <OnboardingLayout type={'QRLinking'}>
        <Fragment>
          <h2 className="QRLinkingAccount__Title">
            {translatedTexts.TITLE_1}
            <br />
            {translatedTexts.TITLE_2}
          </h2>

          <p className="QRLinkingAccount__P1">{translatedTexts.P1_TEXT}</p>

          <p className="QRLinkingAccount__P2">
            {translatedTexts.P2_TEXT_1}
            <br />
            {translatedTexts.P2_TEXT_2}
          </p>

          <p className="QRLinkingAccount__email">
            {renderMaxLengthText(qrUserData?.email, 33)}
          </p>

          <OnboardingButton
            variant="secondary"
            size="large"
            onClick={onFinishAction}
            className="QRLinkingAccount__MainButton"
            textContent={translatedTexts.CONTINUE_BUTTON}
          />

          <p className="QRLinkingAccount__P3__P1">
            {translatedTexts.P3_PART_1}
          </p>

          <p className="QRLinkingAccount__P3__P2">
            {translatedTexts.P3_PART_2}
            <br />
            {translatedTexts.P3_PART_3}
          </p>
        </Fragment>
      </OnboardingLayout>
    </Fragment>
  );
};
